/**
 * Onglet "Statistiques & Comptable" du Super Admin Crédits.
 * Filtres par période et par utilisateur, agrégats journaliers et top users.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { TrendingUp, TrendingDown, Activity, Download, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const CreditStatsAdvancedTab = ({ getAuthHeader }) => {
  const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
  const [dateTo, setDateTo] = useState(todayISO());
  const [role, setRole] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (role && role !== 'all') params.append('role', role);
      const res = await axios.get(
        `${API}/api/admin/credit-usage-plans/stats/period?${params}`,
        { headers: getAuthHeader() }
      );
      setData(res.data);
    } catch {
      toast.error('Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, dateFrom, dateTo, role]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data?.par_utilisateur) return;
    const header = 'email,nom,prenom,role,credits_emis,credits_consommes,nb_transactions\n';
    const rows = data.par_utilisateur.map((u) =>
      [u.email, u.nom, u.prenom, u.role, u.credits_emis, u.credits_consommes, u.nb_transactions]
        .map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credits-stats-${dateFrom}_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  const totaux = data?.totaux || {};
  const maxDaily = Math.max(1, ...(data?.par_jour || []).map((d) => Math.max(d.emis, d.consommes)));

  return (
    <div className="space-y-4" data-testid="credit-stats-advanced-tab">
      {/* Filtres */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-5 w-5 text-indigo-500" />
            Filtres de période
          </CardTitle>
          <CardDescription>Analysez la consommation et l'émission de crédits sur une plage donnée.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs">Du</Label>
              <DatePickerFr className="w-full" value={dateFrom} onChange={setDateFrom} id="stats-date-from" />
            </div>
            <div>
              <Label className="text-xs">Au</Label>
              <DatePickerFr className="w-full" value={dateTo} onChange={setDateTo} id="stats-date-to" />
            </div>
            <div>
              <Label className="text-xs">Rôle</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="stats-role-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="cooper">Coopers</SelectItem>
                  <SelectItem value="member">Membres</SelectItem>
                  <SelectItem value="expert">Experts</SelectItem>
                  <SelectItem value="super_admin">Super Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={load} disabled={loading} variant="outline" data-testid="stats-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Rafraîchir
            </Button>
            <Button onClick={exportCSV} disabled={!data?.par_utilisateur?.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="stats-export-csv">
              <Download className="h-4 w-4 mr-1" />Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Crédits émis</p>
          <p className="text-2xl font-bold text-emerald-600">{(totaux.credits_emis || 0).toLocaleString()}</p>
          <p className="text-[11px] text-slate-400">(achats + bonus + attributions)</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Crédits consommés</p>
          <p className="text-2xl font-bold text-red-500">{(totaux.credits_consommes || 0).toLocaleString()}</p>
          <p className="text-[11px] text-slate-400">(usages site)</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Attributions manuelles</p>
          <p className="text-2xl font-bold text-indigo-600">{(totaux.credits_attribues_manuel || 0).toLocaleString()}</p>
          <p className="text-[11px] text-slate-400">(par Super Admin)</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Transactions</p>
          <p className="text-2xl font-bold text-slate-700">{(totaux.nb_transactions || 0).toLocaleString()}</p>
          <p className="text-[11px] text-slate-400">(lignes journal)</p>
        </CardContent></Card>
      </div>

      {/* Série journalière */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Activité quotidienne</CardTitle>
          <CardDescription>Crédits émis (vert) et consommés (rouge) par jour.</CardDescription>
        </CardHeader>
        <CardContent>
          {data?.par_jour?.length ? (
            <div className="space-y-1 max-h-80 overflow-y-auto pr-2">
              {data.par_jour.map((row) => (
                <div key={row.date} className="grid grid-cols-12 gap-2 items-center text-xs">
                  <span className="col-span-2 text-slate-500 font-mono">{row.date}</span>
                  <div className="col-span-5 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-emerald-500" />
                    <div className="flex-1 bg-emerald-50 rounded">
                      <div className="h-3 rounded bg-emerald-400" style={{ width: `${(row.emis / maxDaily) * 100}%` }} />
                    </div>
                    <span className="w-14 text-right text-emerald-700">{row.emis}</span>
                  </div>
                  <div className="col-span-5 flex items-center gap-1">
                    <TrendingDown className="h-3 w-3 text-red-500" />
                    <div className="flex-1 bg-red-50 rounded">
                      <div className="h-3 rounded bg-red-400" style={{ width: `${(row.consommes / maxDaily) * 100}%` }} />
                    </div>
                    <span className="w-14 text-right text-red-700">{row.consommes}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic text-center py-4">Aucune activité sur cette période.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top utilisateurs */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top utilisateurs</CardTitle>
            <CardDescription>Triés par consommation décroissante.</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.par_utilisateur?.length ? (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {data.par_utilisateur.slice(0, 25).map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between px-2 py-1.5 text-xs border-b last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate text-slate-700">
                        {u.prenom || ''} {u.nom || ''} <span className="text-slate-400">— {u.email}</span>
                      </div>
                      <div className="text-[11px] text-slate-400">
                        <Badge variant="outline" className="text-[10px] mr-1">{u.role || 'N/A'}</Badge>
                        {u.nb_transactions} transaction(s)
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-emerald-600 font-medium">+{u.credits_emis}</span>{' / '}
                      <span className="text-red-600 font-medium">-{u.credits_consommes}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic text-center py-4">Aucun utilisateur actif.</p>
            )}
          </CardContent>
        </Card>

        {/* Ventilation par action */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ventilation par action consommée</CardTitle>
            <CardDescription>Quels usages mobilisent le plus de crédits ?</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.par_action?.length ? (
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {data.par_action.map((a) => (
                  <div key={a.code} className="flex items-center justify-between gap-2 text-xs border-b last:border-0 py-1.5">
                    <span className="truncate">
                      <span className="font-medium text-slate-700">{a.libelle}</span>{' '}
                      <span className="text-slate-400">({a.code})</span>
                    </span>
                    <span className="whitespace-nowrap">
                      <Badge variant="secondary">{a.credits_consommes} cr.</Badge>{' '}
                      <span className="text-slate-400 text-[11px]">×{a.nb_operations}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic text-center py-4">
                Aucune action consommatrice sur cette période. Les actions doivent enregistrer
                <code className="mx-1 px-1 bg-slate-100 rounded">action_code</code>/
                <code className="px-1 bg-slate-100 rounded">action_label</code> dans
                <code className="mx-1 px-1 bg-slate-100 rounded">credit_transactions</code>.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreditStatsAdvancedTab;
